import { Space } from 'antd';
import { useFormikContext } from 'formik';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SuitCaseIcon from 'components/icons/SuitCaseIcon';
import Button from 'components/new-ui/base/button/Button';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import useJobs from 'hooks/useJobs';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import useManifests from 'hooks/useManifests';
import { CreateManifestJobFormFields, CreateManifestJobFormSteps } from './types';
import Notification from 'components/ui/base/notification/Notification';
import { assignOwnerToJob } from 'store/jobs/client';
import { addJobToManifest } from 'store/manifests/client';
import { getErrorMessage } from 'utils/getErrorMessage';

const CreateManifestJobHeader: React.FC = () => {
    const navigate = useNavigate();
    const { isReadyToSubmit, isSubmitting } = useMultiStepFormContext();
    const { manifest } = useManifests();
    const { manifestId } = useParams<{ manifestId: string }>();
    const { values } = useFormikContext<CreateManifestJobFormFields>();
    const location = useLocation();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { createJob } = useJobs();

    const navigateOut = () => {
        if (location.key !== 'default') {
            navigate(-1);
        } else {
            navigate('/manifests', { replace: true });
        }
    };

    const handleCancel = () => {
        navigateOut();
    };

    const handleSubmit = async () => {
        try {
            if (!manifestId || !manifest?.id || !manifest?.team?.id) {
                Notification({
                    placement: 'top',
                    type: 'error',
                    messageTitle: 'Error',
                    description: 'Could not create job under this manifest as manifest data is missing',
                });

                return;
            }

            showGlobalOverlay({
                type: 'LoadingOverlay',
                payload: {
                    message: 'Creating job...',
                },
            });

            // Create the job
            const createdJob = await createJob({
                referenceId: values[CreateManifestJobFormSteps.JOB].jobId,
                customerId: values[CreateManifestJobFormSteps.CUSTOMER].customerId,
                incotermCode: values[CreateManifestJobFormSteps.DELIVERY_TERMS].incotermCode,
                teamId: manifest.team.id,
            });

            if (!createdJob.id) {
                throw new Error('Could not create job. Please try again');
            }

            // Link the job with the manifest
            await addJobToManifest({
                manifestId: manifest.id,
                jobId: createdJob.id,
            });

            Notification({
                placement: 'top',
                type: 'success',
                messageTitle: 'Job Created',
                description: 'Successfully created job',
            });

            // If we have selected an agent to assign
            if (values[CreateManifestJobFormSteps.ASSIGN_AGENT].assignedAgent) {
                await assignOwnerToJob(createdJob.id, values[CreateManifestJobFormSteps.ASSIGN_AGENT].assignedAgent);

                Notification({
                    placement: 'top',
                    type: 'success',
                    messageTitle: 'Job Assigned',
                    description: 'Successfully assigned job to agent',
                });
            }

            navigate(`/manifests/${manifestId}`, { replace: true });
        } catch (err: any) {
            const error = getErrorMessage(err);

            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'Error',
                description: error || 'Something went wrong',
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    return (
        <PageHeader
            title="Create Job"
            icon={<SuitCaseIcon />}
            sidebar={
                <Space size={16}>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button loading={isSubmitting} disabled={!isReadyToSubmit()} onClick={handleSubmit} type="primary">
                        Create Job
                    </Button>
                </Space>
            }
        />
    );
};

export default CreateManifestJobHeader;
